import { styled } from 'dripsy';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ModalComponentProp } from 'react-native-modalfy';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View } from 'react-native';

import { Separator } from '../../components/common/Separator';
import { BaseText } from '../../components/common/BaseText';
import { ModalStackParamsType } from '../../modals/types';
import { Button, ButtonStyleEnum } from '../common/Button';
import { BottomModalView } from './components/BottomModalView';
import type { INotificationsService } from '../../services/NotificationsService';

const ModalViewContainer = styled(BottomModalView)({
  px: '$6',
});

const TopLine = styled(View)({
  backgroundColor: '$background30',
  height: 6,
  width: 60,
  borderRadius: 3,
  alignSelf: 'center',
});

const Title = styled(BaseText)({
  textAlign: 'center',
  variant: 'text.xxlarge',
  color: '$heading900',
  fontWeight: 'semi',
});

const Subtitle = styled(BaseText)({
  textAlign: 'center',
  variant: 'text.small',
  color: '$text90',
});

export interface IAllowNotificationsModalParams {
  onAllow: INotificationsService['requestPermission'];
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'AllowNotificationsModal'
>;

export function AllowNotificationsModal({ modal }: ModalPropsType) {
  const { bottom } = useSafeAreaInsets();
  const { t } = useTranslation();

  async function allowNotifications() {
    await modal.params?.onAllow();
    closeModal();
  }

  function closeModal() {
    modal.closeModal();
  }

  return (
    <ModalViewContainer bottomInset={bottom}>
      <Separator />
      <TopLine />

      <Separator size="$7" />
      <Title>{t('common.allowNotifications.title')}</Title>

      <Separator size="$5" />
      <Subtitle>{t('common.allowNotifications.subtitle')}</Subtitle>

      <Separator size="$8" />
      <Button onPress={allowNotifications}>
        {t('common.allowNotifications.allow')}
      </Button>

      <Separator size="$5" />
      <Button
        onPress={closeModal}
        buttonStyle={ButtonStyleEnum.Outline}
      >
        {t('common.allowNotifications.cancel')}
      </Button>

      <Separator size="$9" />
    </ModalViewContainer>
  );
}
